import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Brain, MapPin, BarChart3, Target } from "lucide-react";
import { api, formatINR, formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/images";

const OBJECTIVES = [
  { value: "awareness", label: "Brand awareness" },
  { value: "launch", label: "Product / brand launch" },
  { value: "footfall", label: "Drive store footfall" },
  { value: "conversion", label: "Conversion / leads" },
  { value: "engagement", label: "Engagement & sampling" },
];
const INDUSTRIES = ["Automotive", "FMCG / Beauty", "Fashion & Luxury", "BFSI", "Telecom", "OTT / Entertainment", "F&B / QSR", "Real Estate", "Healthcare", "Education", "Other"];
const FORMATS = ["Backlit", "Pillar Branding", "Facade", "Atrium Space", "LED Screen", "Escalator Branding", "Standee", "Lift Branding", "Kiosk", "Sampling Booth", "Hoarding", "Dropdown", "Food Court"];
const CITIES = ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kochi", "Pune", "Kolkata", "Ahmedabad", "Calicut", "Chandigarh", "Lucknow", "Coimbatore", "Thiruvanathapuram", "Noida", "Gurugram"];
const PROVIDERS = [
  { value: "gemini", label: "Gemini 3 Flash · fastest" },
  { value: "anthropic", label: "Claude Haiku 4.5" },
  { value: "openai", label: "GPT-5.4 mini" },
];

export default function AIPlanner() {
  const [form, setForm] = useState({
    objective: "awareness", industry: "FMCG / Beauty",
    target_audience: "", cities: [],
    budget_inr: 5000000, duration_weeks: 4,
    formats_preference: [], notes: "", provider: "gemini",
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", company: "" });

  const toggleArr = (field, value) =>
    setForm((f) => ({ ...f, [field]: f[field].includes(value) ? f[field].filter((x) => x !== value) : [...f[field], value] }));

  async function generate(e) {
    e.preventDefault();
    if (!form.target_audience) { toast.error("Tell us who you want to reach."); return; }
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.post("/public/ai-planner", form);
      setPlan(data.plan);
      toast.success("Your AI media plan is ready.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate plan.");
    } finally { setLoading(false); }
  }

  async function requestProposal() {
    if (!contact.name || !contact.email) { toast.error("Share your name and email."); return; }
    try {
      await api.post("/public/leads", {
        ...contact,
        source: "ai_planner",
        message: `AI Planner: ${form.objective} · ${form.industry} · ${form.cities.join(", ")} · ${formatINR(form.budget_inr)} · ${form.duration_weeks}w. Headline: ${plan?.headline}`,
      });
      toast.success("Proposal request received.");
      setContact({ name: "", email: "", company: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="ai-planner-page" className="dark-mode bg-[var(--obsidian)] text-[var(--bone)] min-h-screen">
      {/* Banner */}
      <section className="relative h-[60vh] lg:h-[68vh] overflow-hidden">
        <img src={IMG.aiPlannerMag} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--obsidian)]/40 via-[var(--obsidian)]/60 to-[var(--obsidian)]" />
        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-12 lg:pb-20">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)] flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[var(--gold-light)]" /> AI Media Planner
          </div>
          <h1 className="mt-5 font-serif-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white max-w-5xl leading-[0.95]">
            Brief our AI. <br className="hidden md:block" />Receive a <span className="font-serif-italic text-[var(--gold-light)]">cinema-grade media plan.</span>
          </h1>
          <p className="mt-6 text-white/80 text-lg lg:text-xl max-w-2xl">
            Recommended cities, malls, formats, budget split, KPIs, week-by-week timeline and
            creative directions — with the reasoning behind every recommendation.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Form */}
        <form className="lg:col-span-5 surface-dark p-8 lg:p-10 space-y-5 self-start sticky top-28" onSubmit={generate} data-testid="ai-planner-form">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">01 · Brief</div>
          <Field label="Campaign objective">
            <select data-testid="ai-objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>
              {OBJECTIVES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Industry">
            <select data-testid="ai-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Target audience">
            <textarea data-testid="ai-audience" rows={2} value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} placeholder="e.g. urban affluent women, 25-40, premium beauty buyers" />
          </Field>
          <Field label="Cities (optional)">
            <div className="flex flex-wrap gap-1.5">
              {CITIES.map((c) => (
                <button key={c} type="button" onClick={() => toggleArr("cities", c)} data-testid={`ai-city-${c}`}
                  className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition ${
                    form.cities.includes(c)
                      ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                      : "bg-transparent text-white/75 border-white/25 hover:border-[var(--gold-light)]"
                  }`}
                >{c}</button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Budget (INR)">
              <input data-testid="ai-budget" type="number" value={form.budget_inr} min={500000} step={500000} onChange={(e) => setForm({ ...form, budget_inr: Number(e.target.value) })} />
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--gold-light)]">{formatINR(form.budget_inr)}</div>
            </Field>
            <Field label="Duration (weeks)">
              <input data-testid="ai-duration" type="number" min={1} max={26} value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: Number(e.target.value) })} />
            </Field>
          </div>
          <Field label="Preferred formats">
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-auto">
              {FORMATS.map((f) => (
                <button key={f} type="button" onClick={() => toggleArr("formats_preference", f)}
                  className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition ${
                    form.formats_preference.includes(f) ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                    : "bg-transparent text-white/75 border-white/25 hover:border-[var(--gold-light)]"
                  }`}
                >{f}</button>
              ))}
            </div>
          </Field>
          <Field label="Brand notes">
            <textarea data-testid="ai-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Context — launch dates, hero SKU, tone…" />
          </Field>
          <Field label="AI provider">
            <select data-testid="ai-provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <button type="submit" data-testid="ai-submit" className="btn-primary w-full justify-center !bg-[var(--gold)] !border-[var(--gold)] !text-white hover:!bg-[var(--bone)] hover:!text-[var(--obsidian)] hover:!border-[var(--bone)]" disabled={loading}>
            <Sparkles size={14} /> {loading ? "Composing plan…" : "Generate plan"}
          </button>
        </form>

        {/* Output */}
        <div className="lg:col-span-7" data-testid="ai-plan-output">
          {loading && <PlanSkeleton />}
          {!loading && !plan && (
            <div className="border border-white/15 p-12 lg:p-16 text-center">
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">Awaiting brief</div>
              <Sparkles size={32} className="text-[var(--gold-light)] mx-auto mt-6" />
              <div className="mt-5 font-serif-display text-3xl text-white">Your media plan will appear here.</div>
              <p className="mt-3 text-white/65 text-sm max-w-md mx-auto">
                Fill in the brief and hit <span className="text-[var(--gold-light)]">Generate plan</span>. Average compose time: 8–30 seconds.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-3 max-w-md mx-auto">
                {[
                  { icon: Brain, label: "Frontier LLMs" },
                  { icon: MapPin, label: "Real Indian inventory" },
                  { icon: BarChart3, label: "Reach + impressions" },
                  { icon: Target, label: "Measurable KPIs" },
                ].map((p) => (
                  <div key={p.label} className="border border-white/15 p-3 flex items-center gap-2">
                    <p.icon size={14} className="text-[var(--gold-light)]" />
                    <span className="text-xs text-white/80 uppercase tracking-[0.15em]">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!loading && plan && <PlanCard plan={plan} contact={contact} setContact={setContact} requestProposal={requestProposal} />}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--gold-light)] mb-2">{label}</div>
      {children}
    </label>
  );
}

function PlanSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="border border-white/10 p-6 h-28 animate-pulse" />
      ))}
    </div>
  );
}

function PlanCard({ plan, contact, setContact, requestProposal }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-6">
      <div className="border border-white/15 bg-white/[0.03] p-8 lg:p-10 border-l-2 !border-l-[var(--gold)]" data-testid="plan-headline">
        <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">Plan headline</div>
        <h2 className="mt-4 font-serif-display text-3xl lg:text-4xl text-white leading-tight">{plan.headline}</h2>
        <p className="mt-5 text-white/75 leading-relaxed">{plan.rationale}</p>
      </div>

      <div className="grid grid-cols-3 gap-px bg-white/15" data-testid="plan-metrics">
        <Metric label="Est. reach" value={`${plan.estimated_reach_millions}M`} />
        <Metric label="Est. impressions" value={`${plan.estimated_impressions_millions}M`} />
        <Metric label="Timeline" value={`${plan.timeline?.length || 0} wk`} />
      </div>

      <PlanSection title="Recommended cities" testId="plan-cities">
        {plan.recommended_cities?.map((c, i) => <Row key={i} primary={c.city} chip={`${c.weight_pct}%`} note={c.why} />)}
      </PlanSection>
      <PlanSection title="Recommended formats" testId="plan-formats">
        {plan.recommended_formats?.map((f, i) => <Row key={i} primary={f.format} chip={`${f.weight_pct}%`} note={f.why} />)}
      </PlanSection>
      <PlanSection title="Hand-picked malls" testId="plan-malls">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {plan.recommended_malls?.map((m, i) => (
            <div key={i} className="bg-white/[0.04] border border-white/15 p-4">
              <div className="font-serif text-lg text-white">{m.mall}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--gold-light)] mt-1">{m.city} · {m.format}</div>
              <div className="text-sm text-white/70 mt-2">{m.why}</div>
            </div>
          ))}
        </div>
      </PlanSection>
      <PlanSection title="Budget allocation" testId="plan-budget">
        <div className="space-y-3">
          {plan.budget_allocation?.map((b, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-white">{b.bucket}</span>
                <span className="text-white/55 text-xs font-mono">{formatINR(b.amount_inr)} · {b.share_pct}%</span>
              </div>
              <div className="mt-1.5 h-1.5 bg-white/10">
                <div className="h-full bg-[var(--gold)]" style={{ width: `${Math.min(100, b.share_pct)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </PlanSection>
      {plan.ideal_activations?.length > 0 && (
        <PlanSection title="Ideal experiential activations">
          <ul className="space-y-2 text-sm text-white/85">
            {plan.ideal_activations.map((a, i) => (
              <li key={i} className="flex gap-3"><span className="text-[var(--gold-light)]">—</span>{a}</li>
            ))}
          </ul>
        </PlanSection>
      )}
      {plan.recommended_creatives?.length > 0 && (
        <PlanSection title="Recommended creative directions">
          <ul className="space-y-2 text-sm text-white/85">
            {plan.recommended_creatives.map((a, i) => (
              <li key={i} className="flex gap-3"><span className="text-[var(--gold-light)]">—</span>{a}</li>
            ))}
          </ul>
        </PlanSection>
      )}
      {plan.kpis?.length > 0 && (
        <PlanSection title="KPIs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {plan.kpis.map((k, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/15 p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold-light)]">{k.name}</div>
                <div className="mt-1 text-white text-sm">{k.target}</div>
              </div>
            ))}
          </div>
        </PlanSection>
      )}
      {plan.timeline?.length > 0 && (
        <PlanSection title="Week by week">
          <div className="space-y-2">
            {plan.timeline.map((t, i) => (
              <div key={i} className="flex gap-4">
                <div className="text-[var(--gold-light)] text-xs font-mono w-12 shrink-0 pt-0.5">W{t.week}</div>
                <div className="text-sm text-white/85">{t.milestone}</div>
              </div>
            ))}
          </div>
        </PlanSection>
      )}

      <div className="border border-white/15 bg-white/[0.03] p-8 lg:p-10" data-testid="plan-cta">
        <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">Lock this plan</div>
        <h3 className="mt-3 font-serif-display text-2xl lg:text-3xl text-white">Convert into a buyable proposal — in 24 hours.</h3>
        <p className="mt-3 text-white/65 text-sm">We&apos;ll firm up pricing, check availability and send a signed quote.</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="plan-cta-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Name" />
          <input data-testid="plan-cta-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Work email" type="email" />
          <input data-testid="plan-cta-company" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company" />
        </div>
        <button data-testid="plan-cta-submit" onClick={requestProposal} className="mt-5 btn-primary !bg-[var(--gold)] !border-[var(--gold)] !text-white">
          Request live proposal <ArrowRight size={14} />
        </button>
      </div>
    </motion.div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-[var(--obsidian)] p-6 text-center">
      <div className="font-serif-display text-3xl lg:text-4xl text-[var(--gold-light)]">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</div>
    </div>
  );
}

function PlanSection({ title, children, testId }) {
  return (
    <div className="border border-white/15 p-6 lg:p-7" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Row({ primary, chip, note }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 p-3 flex items-start gap-3 mb-2">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <span className="font-serif text-lg text-white">{primary}</span>
          {chip && <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold-light)] border border-[var(--gold-light)]/40 px-2 py-0.5">{chip}</span>}
        </div>
        {note && <div className="mt-1 text-xs text-white/70">{note}</div>}
      </div>
    </div>
  );
}
